import { createTheme } from '@mui/material/styles';

export const tokens = {
  primary: '#465FFF',
  primaryHover: '#3641F5',
  primaryTint: '#EEF1FF',
  ground: '#F9FAFB',
  card: '#FFFFFF',
  border: '#E4E7EC',
  text: '#1C2434',
  muted: '#64748B',
  success: '#079455',
  error: '#B42318',
  warning: '#B54708',
  info: '#175CD3',
  shadow: '0 1px 2px rgba(16,24,40,0.05)',
  radius: 14,
  font: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
};

export const glass = {
  mesh: 'radial-gradient(40% 50% at 8% 6%, #EEF1FF 0%, rgba(238,241,255,0) 100%), '
    + 'radial-gradient(35% 45% at 92% 12%, #E0F7FB 0%, rgba(224,247,251,0) 100%), '
    + 'radial-gradient(45% 50% at 55% 98%, #F5EEFF 0%, rgba(245,238,255,0) 100%), #F9FAFB',
  card: 'rgba(255,255,255,0.72)',
  header: 'rgba(255,255,255,0.88)',
  overlay: 'rgba(255,255,255,0.85)',
  dialog: 'rgba(255,255,255,0.96)',
  dense: 'rgba(255,255,255,0.9)',
  border: '1px solid rgba(255,255,255,0.6)',
  shadow: '0 8px 32px rgba(31,38,135,0.10)'
};

export const glassFallback = (solid = { background: '#FFFFFF' }) => ({
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': solid,
  '@media (prefers-reduced-transparency: reduce)': { ...solid, backdropFilter: 'none', WebkitBackdropFilter: 'none' }
});

const frosted = (background, blur, extra = '') => ({
  background,
  backdropFilter: `blur(${blur}px)${extra}`,
  WebkitBackdropFilter: `blur(${blur}px)${extra}`,
  ...glassFallback()
});

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: tokens.primary, dark: tokens.primaryHover, light: tokens.primaryTint, contrastText: '#FFFFFF' },
    secondary: { main: tokens.text, contrastText: '#FFFFFF' },
    background: { default: tokens.ground, paper: tokens.card },
    error: { main: tokens.error },
    warning: { main: tokens.warning },
    success: { main: tokens.success },
    info: { main: tokens.info },
    text: { primary: tokens.text, secondary: tokens.muted },
    divider: tokens.border
  },
  typography: {
    fontFamily: tokens.font,
    h4: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontSize: 20, fontWeight: 700 },
    h6: { fontSize: 16, fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600 }
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes svx-shake': {
          '0%, 100%': { transform: 'none' },
          '20%': { transform: 'translateX(-8px)' },
          '40%': { transform: 'translateX(7px)' },
          '60%': { transform: 'translateX(-5px)' },
          '80%': { transform: 'translateX(3px)' }
        },
        'html, body': { overscrollBehaviorY: 'contain' },
        body: { background: glass.mesh, backgroundAttachment: 'fixed' },
        '.svx-shake': { animation: 'svx-shake .4s' },
        '@keyframes svx-tab-fill': {
          from: { clipPath: 'circle(0% at 50% 50%)' },
          to: { clipPath: 'circle(150% at 50% 50%)' }
        },
        '@keyframes svx-squish-on': { '50%': { transform: 'scaleX(1.3)', transformOrigin: 'right center' } },
        '@keyframes svx-squish-off': { '50%': { transform: 'scaleX(1.3)', transformOrigin: 'left center' } },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius,
          border: glass.border,
          boxShadow: glass.shadow,
          ...frosted(glass.card, 16, ' saturate(140%)')
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, minHeight: 40, padding: '6px 18px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        sizeSmall: { minHeight: 32, padding: '4px 12px' },
        containedPrimary: { '&:hover': { backgroundColor: tokens.primaryHover } }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 10, backgroundColor: glass.dense } }
    },
    MuiAppBar: {
      styleOverrides: { root: { backgroundImage: 'none' } }
    },
    MuiTableContainer: {
      styleOverrides: { root: { backgroundColor: glass.dense } }
    },
    MuiDialog: {
      styleOverrides: { paper: { border: glass.border, boxShadow: glass.shadow, ...frosted(glass.dialog, 24) } }
    },
    MuiBackdrop: {
      styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(16,24,40,0.4)' } } }
    },
    MuiPopover: {
      styleOverrides: { paper: { border: glass.border, boxShadow: glass.shadow, ...frosted(glass.overlay, 24) } }
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 999, fontWeight: 500 } }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#F2F4F7' },
        head: { color: tokens.muted, fontSize: 12, fontWeight: 600 }
      }
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-rippleVisible': { opacity: 0.28, animationDuration: '450ms' },
          '& .MuiTouchRipple-childLeaving': { animationDuration: '400ms' }
        }
      }
    },
    MuiIconButton: {
      defaultProps: { centerRipple: false }
    },
    MuiTabs: {
      styleOverrides: { indicator: { display: 'none' } }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          position: 'relative',
          isolation: 'isolate',
          borderRadius: 10,
          transition: 'color 200ms',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 4,
            zIndex: -1,
            borderRadius: 8,
            background: 'radial-gradient(circle at center, #7C3AED 0%, #0E7490 100%)',
            boxShadow: '0 0 12px rgba(6,182,212,0.45)',
            opacity: 0,
            clipPath: 'circle(150% at 50% 50%)',
            transition: 'opacity 200ms ease-out'
          },
          '&.Mui-selected': { color: '#FFFFFF' },
          '&.Mui-selected::before': { opacity: 1, animation: 'svx-tab-fill 350ms cubic-bezier(.2,.8,.2,1)' }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          transition: 'transform 200ms cubic-bezier(.2,.8,.2,1)',
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1,
            backgroundSize: '100% 100%',
            boxShadow: '0 0 6px 1px rgba(6,182,212,.55), 0 0 12px 2px rgba(70,95,255,.35)'
          },
          '&.Mui-checked .MuiSwitch-thumb': { animation: 'svx-squish-on 200ms ease-out' },
          '&:not(.Mui-checked) .MuiSwitch-thumb': { animation: 'svx-squish-off 200ms ease-out' }
        },
        track: {
          opacity: 1,
          backgroundColor: '#98A2B3',
          backgroundImage: 'linear-gradient(#465FFF, #465FFF)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '0% 100%',
          transition: 'background-size 200ms ease-out, box-shadow 300ms 450ms'
        }
      }
    }
  }
});

export default theme;
